import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatDate } from "../../../core/common/api";
import { canManageUsers } from "../../../core/common/adminPermissions";
import { clubLabel } from "../../../shared/Utils";

const SaleUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [buyer, setBuyer] = useState<any>(null);
  const [children, setChildren] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    buyerId: "",
  });

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiRequest(`/sale-users/${id}`, "GET");
      if (!res.success || !res.user) {
        throw new Error(res.message || "Sale user not found");
      }
      setUser(res.user);
      setBuyer(res.buyer || null);
      setChildren(res.children || []);
      setForm({
        name: res.user.name || "",
        email: res.user.email || "",
        phone: res.user.phone || "",
        buyerId: res.user.buyerId || "",
      });
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load sale user", "error");
      navigate("/sale-users", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canManageUsers() || !id) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        buyerId: form.buyerId.trim(),
      };
      const res = await apiRequest(`/sale-users/${id}`, "PUT", payload);
      if (!res.success) throw new Error(res.message || "Update failed");
      await Swal.fire("Updated", "Sale user saved", "success");
      await load();
    } catch (error: any) {
      Swal.fire("Update Failed", error.message || "Unable to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="empty-state">Loading sale user...</div>;
  }

  if (!user) return null;

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/sale-users" className="btn-sm btn-outline back-link">
            ← Back to Sale Users
          </Link>
          <div className="page-eyebrow">Sale User</div>
          <h1 className="page-title">
            {user.name || "Sale"} <em>{user.memberId || ""}</em>
          </h1>
          <div className="page-sub">
            Club {clubLabel(user.club)} · Source {user.source || "sale"} · Created{" "}
            {formatDate(user.createdAt)}
          </div>
        </div>
        <div className="page-hdr-right">
          <Link
            to={`/users/tree?memberId=${encodeURIComponent(user.memberId || "")}`}
            className="btn-sm btn-outline"
          >
            View in Tree
          </Link>
        </div>
      </div>

      <div className="page-stack">
        <section className="table-card">
          <div className="table-head">
            <h2>Income Buyer</h2>
            <p>Club and placement income for this sale ID credits this registered user.</p>
          </div>
          <div className="admin-card-body">
            {buyer ? (
              <p>
                <strong>{buyer.name}</strong> · {buyer.memberId} ·{" "}
                <Link to={`/users/${buyer._id}`}>Open registered user</Link>
              </p>
            ) : (
              <p className="text-muted-sm">Buyer {user.buyerId || "—"}</p>
            )}
          </div>
        </section>

        <section className="table-card">
          <div className="table-head">
            <h2>Placement Children</h2>
            <p>Direct legs under this sale member ID</p>
          </div>
          <div className="table-scroll">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th>Buyer</th>
                </tr>
              </thead>
              <tbody>
                {children.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="empty-state">
                      No children yet
                    </td>
                  </tr>
                ) : (
                  children.map((child) => (
                    <tr key={child._id}>
                      <td>{child.position || "—"}</td>
                      <td>
                        <Link to={`/sale-users/${child._id}`}>{child.memberId}</Link>
                      </td>
                      <td>{child.name}</td>
                      <td>{child.buyerId || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        {canManageUsers() && (
          <form onSubmit={handleSave} className="table-card">
            <div className="table-head">
              <h2>Edit Sale User</h2>
            </div>
            <div className="admin-card-body admin-form-grid">
              <div className="form-field">
                <label className="modal-label">Name</label>
                <input
                  className="admin-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Email</label>
                <input
                  className="admin-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Phone</label>
                <input
                  className="admin-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label className="modal-label">Buyer Member ID</label>
                <input
                  className="admin-input"
                  value={form.buyerId}
                  onChange={(e) => setForm({ ...form, buyerId: e.target.value })}
                />
              </div>
            </div>
            <div className="detail-footer">
              <button type="submit" className="btn-sm btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SaleUserDetail;
